/* Hair Beauty Ink - homepage redesign: hero promo slider */
(() => {
  if (window.customElements.get('hbi-slider')) return;

  class HbiSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.children);
      this.index = 0;
      this.paused = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onResize = () => {
        this.fitHeight();
        if (this.sameHeights()) this.start();
        else this.stop();
      };
      window.addEventListener('resize', this.onResize);
      this.slides.forEach((slide) => {
        const img = slide.querySelector('img');
        if (img && !img.complete) img.addEventListener('load', this.onResize, { once: true });
      });

      // Re-fit whenever a slide changes size, e.g. when turning a phone sideways swaps the
      // portrait banner for the landscape one after the resize event has already run
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.fitHeight());
        this.slides.forEach((slide) => this.resizeObserver.observe(slide));
      }

      if (this.slides.length < 2) {
        this.fitHeight();
        return;
      }

      this.currentLabel = this.querySelector('[data-current]');
      this.cta = this.querySelector('[data-cta]');
      this.ctaText = this.querySelector('[data-cta-text]');
      const dotsWrap = this.querySelector('[data-dots]');
      this.dots = this.slides.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'hbi-slider__dot';
        dot.setAttribute('aria-label', `Show slide ${i + 1}`);
        dot.addEventListener('click', () => this.go(i, true));
        if (dotsWrap) dotsWrap.appendChild(dot);
        return dot;
      });

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));

      this.track.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.syncFromScroll(), 60);
      }, { passive: true });

      this.autoplay = this.dataset.autoplay === 'true' && !this.reduceMotion;
      this.delay = Math.max(3, parseInt(this.dataset.speed, 10) || 6) * 1000;
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.resume());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.resume());
      this.addEventListener('touchstart', () => this.pause(), { passive: true });

      this.onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', this.onVisibility);

      // Theme editor: show the slide that was selected in the sidebar
      this.onBlockSelect = (event) => {
        const i = this.slides.indexOf(event.target);
        if (i > -1) {
          this.pause();
          this.go(i);
        }
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.update();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    go(i, fromUser) {
      const count = this.slides.length;
      this.index = (i + count) % count;
      this.track.scrollTo({
        left: this.slides[this.index].offsetLeft,
        behavior: this.reduceMotion ? 'auto' : 'smooth'
      });
      this.update();
      if (fromUser) {
        this.stop();
        this.start();
      }
    }

    syncFromScroll() {
      const width = this.track.clientWidth || 1;
      const i = Math.min(Math.max(Math.round(this.track.scrollLeft / width), 0), this.slides.length - 1);
      if (i !== this.index) {
        this.index = i;
        this.update();
      }
    }

    update() {
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.setAttribute('aria-hidden', String(!active));
        slide.tabIndex = active ? 0 : -1;
      });
      this.dots?.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
      if (this.currentLabel) this.currentLabel.textContent = String(this.index + 1).padStart(2, '0');
      this.updateCta();
      this.fitHeight();
    }

    // The "Shop now" button under the banner follows the slide that's showing
    updateCta() {
      if (!this.cta || !this.ctaText) return;
      const slide = this.slides[this.index];
      const label = slide.dataset.ctaLabel;
      this.cta.hidden = !label;
      if (!label) return;
      this.cta.href = slide.getAttribute('href') || '#';
      this.ctaText.textContent = label;
    }

    // Slides can have different heights (e.g. a portrait mobile banner), so the
    // track follows the height of whichever slide is showing.
    fitHeight() {
      const slide = this.slides[this.index];
      if (!slide) return;
      const height = slide.offsetHeight;
      if (height) this.track.style.height = `${height}px`;
    }

    // Auto-rotating between slides of different heights (e.g. a portrait mobile
    // banner next to landscape ones) would make the page jump, so only
    // auto-rotate when every slide is the same height.
    sameHeights() {
      const heights = this.slides.map((slide) => slide.offsetHeight).filter(Boolean);
      return heights.length < 2 || Math.max(...heights) - Math.min(...heights) < 4;
    }

    start() {
      if (!this.autoplay || this.paused || this.timer || this.slides.length < 2 || !this.sameHeights()) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.delay);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    pause() {
      this.paused = true;
      this.stop();
    }

    resume() {
      this.paused = false;
      this.start();
    }
  }

  customElements.define('hbi-slider', HbiSlider);
})();
